import { getLocales } from 'assets/locales/Locale';
import { AutomationInputTypes, EffectTypes, ExpressionTypes } from 'models/enums/Automation';
import ClaimTypeDataPointInputType from 'models/enums/ClaimTypeDataPointInputType';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import {
  AutomationOption,
  AutomationPropertyMetadata,
  AutomationRuleSchema,
} from 'module/automation/helpers/AutomationTypes';

export const ClaimAutomationPropertyPath = {
  totalAmount: 'ClaimJob.TotalAmountCAD',
  claimJobStatus: 'ClaimJob.StatusCode',
  areAutomationRulesApplied: 'ClaimJob.AreAutomationRulesApplied',
  //
  contractExpiryDate: 'ClaimJob.Claim.Contract.ExpiryDate',
  contractEffectiveDate: 'ClaimJob.Claim.Contract.EffectiveDate',
  contractSalePriceCAD: 'ClaimJob.Claim.Contract.SalePriceCAD',
  contractStatus: 'ClaimJob.Claim.Contract.StatusCode',
  contractMaxKm: 'ClaimJob.Claim.Contract.MaxKm',
  contractLegacyProductName: 'ClaimJob.Claim.Contract.LegacyProductName',
  //
  claimCreatedAtUtc: 'ClaimJob.Claim.CreatedAtUtc',
  claimStatus: 'ClaimJob.Claim.StatusCode',
  claimWorkOrderNumber: 'ClaimJob.Claim.WorkOrderNumber',
  claimOdometerReading: 'ClaimJob.Claim.OdometerReading',
  claimAlerts: 'ClaimJob.Claim.ClaimAlerts',
  //
  legacyClaimTypeId: 'ClaimJob.ClaimType.LegacyClaimTypeId',
};

export const ClaimAutomationDataPointPropertyPathPrefix = 'ClaimJob.DataPoints';

export function createClaimDataPointPropertyPath(name?: string) {
  return `${ClaimAutomationDataPointPropertyPathPrefix}[Name=${name || ''}].Value`;
}

export function getClaimDataPointNameFromPropertyPath(propertyPath?: string | null) {
  const match = propertyPath?.match(/^ClaimJob\.DataPoints\[Name=([^\]]+)\](?:\.Value)?$/);
  return match?.[1];
}

export function isClaimDataPointPropertyPath(propertyPath?: string | null): propertyPath is string {
  return Boolean(getClaimDataPointNameFromPropertyPath(propertyPath));
}

function getClaimDataPointPropertyMetadata(
  dataPoint: Partial<Model.IClaimTypeDataPoint>,
  fallbackExpressionTypes: Model.Enum.Automation.ExpressionTypes[]
): AutomationPropertyMetadata {
  const typedExpressionTypes: Partial<
    Record<Model.Enum.ClaimTypeDataPointInputType, Model.Enum.Automation.ExpressionTypes[]>
  > = {
    [ClaimTypeDataPointInputType.Number]: [
      ExpressionTypes.Equal,
      ExpressionTypes.GreaterThan,
      ExpressionTypes.GreaterThanOrEqual,
      ExpressionTypes.LessThan,
      ExpressionTypes.LessThanOrEqual,
      ExpressionTypes.Range,
      ExpressionTypes.Any,
      ExpressionTypes.ValueNumeric,
      ExpressionTypes.ValueProperty,
    ],
    [ClaimTypeDataPointInputType.Price]: [
      ExpressionTypes.Equal,
      ExpressionTypes.GreaterThan,
      ExpressionTypes.GreaterThanOrEqual,
      ExpressionTypes.LessThan,
      ExpressionTypes.LessThanOrEqual,
      ExpressionTypes.Range,
      ExpressionTypes.Any,
      ExpressionTypes.ValueNumeric,
      ExpressionTypes.ValueProperty,
    ],
    [ClaimTypeDataPointInputType.Date]: [
      ExpressionTypes.Equal,
      ExpressionTypes.GreaterThan,
      ExpressionTypes.GreaterThanOrEqual,
      ExpressionTypes.LessThan,
      ExpressionTypes.LessThanOrEqual,
      ExpressionTypes.Any,
      ExpressionTypes.ValueDateTime,
      ExpressionTypes.ValueProperty,
    ],
    [ClaimTypeDataPointInputType.Boolean]: [
      ExpressionTypes.Equal,
      ExpressionTypes.Any,
      ExpressionTypes.ValueBoolean,
      ExpressionTypes.ValueProperty,
    ],
  };

  return {
    inputType:
      dataPoint.type === ClaimTypeDataPointInputType.Boolean ? AutomationInputTypes.Boolean : AutomationInputTypes.Text,
    supportedExpressions: typedExpressionTypes[dataPoint.type] || fallbackExpressionTypes,
  };
}

type ClaimAutomationEffectForCleanup = Partial<
  Pick<Model.Automation.IEffect, 'propertyPath' | 'sourcePropertyPath'>
> & {
  [key: string]: any;
};

type ClaimAutomationRuleForDataPointVisibility = {
  effects?: Array<Partial<Pick<Model.Automation.IEffect, 'propertyPath' | 'type'>>>;
};

function isActiveClaimDataPointPath(propertyPath: string | undefined, activeDataPointNames: Set<string>) {
  const dataPointName = getClaimDataPointNameFromPropertyPath(propertyPath);
  return Boolean(dataPointName && activeDataPointNames.has(dataPointName));
}

export function getAssignFromSourceDataPointEffectNames(
  rules: ClaimAutomationRuleForDataPointVisibility[] | undefined
) {
  return new Set(
    rules?.flatMap(
      (rule) =>
        rule.effects?.flatMap((effect) => {
          if (effect.type !== EffectTypes.AssignFromSource) return [];

          const dataPointName = getClaimDataPointNameFromPropertyPath(effect.propertyPath);
          return dataPointName ? [dataPointName] : [];
        }) || []
    ) || []
  );
}

export function createClaimTypeDataPointVisibility(rules: ClaimAutomationRuleForDataPointVisibility[] | undefined) {
  const automatedDataPointNames = getAssignFromSourceDataPointEffectNames(rules);
  return (dataPointName?: string) => !dataPointName || !automatedDataPointNames.has(dataPointName);
}

export function filterAutomatedClaimTypeDataPoints<T extends { name?: string }>(
  dataPoints: T[] | undefined,
  rules: ClaimAutomationRuleForDataPointVisibility[] | undefined
) {
  const automatedDataPointNames = getAssignFromSourceDataPointEffectNames(rules);

  return dataPoints?.filter((dataPoint) => !dataPoint.name || !automatedDataPointNames.has(dataPoint.name));
}

export function cleanClaimDataPointEffects<T extends { effects?: ClaimAutomationEffectForCleanup[] }>(
  rules: T[] | undefined,
  claimTypeDataPoints: Partial<Model.IClaimTypeDataPoint>[] | undefined
) {
  const activeDataPointNames = new Set(
    claimTypeDataPoints?.filter((dataPoint) => dataPoint.name).map((dataPoint) => dataPoint.name as string)
  );

  return rules?.map(
    (rule) =>
      ({
        ...rule,
        effects:
          rule.effects
            ?.filter(
              (effect) =>
                !isClaimDataPointPropertyPath(effect.propertyPath) ||
                isActiveClaimDataPointPath(effect.propertyPath, activeDataPointNames)
            )
            .map((effect) => {
              const sourcePropertyPath = effect.sourcePropertyPath;

              if (
                isClaimDataPointPropertyPath(sourcePropertyPath) &&
                !isActiveClaimDataPointPath(sourcePropertyPath, activeDataPointNames)
              ) {
                return { ...effect, sourcePropertyPath: undefined };
              }

              return effect;
            }) || [],
      }) as T
  );
}

type ClaimAutomationSchemaArgs = {
  claimStatuses: AutomationOption[];
  claimJobStatuses: AutomationOption[];
  claimTypeDataPoints?: Partial<Model.IClaimTypeDataPoint>[];
};

export function createClaimAutomationSchema({
  claimStatuses,
  claimJobStatuses,
  claimTypeDataPoints,
}: ClaimAutomationSchemaArgs): AutomationRuleSchema {
  const { translate } = getLocales();
  const claimExpressionTypes = Object.values(ExpressionTypes);
  const claimDateExpressionTypes = claimExpressionTypes.filter((type) => type !== ExpressionTypes.Range);
  const claimNumericExpressionTypes = [
    ExpressionTypes.Equal,
    ExpressionTypes.GreaterThan,
    ExpressionTypes.GreaterThanOrEqual,
    ExpressionTypes.LessThan,
    ExpressionTypes.LessThanOrEqual,
    ExpressionTypes.Range,
    ExpressionTypes.Any,
    ExpressionTypes.ValueNumeric,
    ExpressionTypes.ValueProperty,
  ];
  const claimTextExpressionTypes = [
    ExpressionTypes.Equal,
    ExpressionTypes.Contains,
    ExpressionTypes.Any,
    ExpressionTypes.ValueString,
    ExpressionTypes.ValueProperty,
  ];
  const claimBooleanExpressionTypes = [
    ExpressionTypes.Equal,
    ExpressionTypes.Any,
    ExpressionTypes.ValueBoolean,
    ExpressionTypes.ValueProperty,
  ];
  const claimStatusExpressionTypes = [
    ExpressionTypes.Equal,
    ExpressionTypes.Any,
    ExpressionTypes.ValueString,
    ExpressionTypes.ValueProperty,
  ];
  const contractStatusOptions = Object.values(ContractStatusCode).map((status) => ({
    id: status,
    title: translate(status),
  }));
  const claimDataPointEffects =
    claimTypeDataPoints?.flatMap((dataPoint) => {
      if (!dataPoint.name) return [];

      return [
        {
          type: 'assign' as const,
          propertyPath: createClaimDataPointPropertyPath(dataPoint.name),
          supportedEffectTypes: [EffectTypes.Assign, EffectTypes.AssignFromSource],
        },
      ];
    }) || [];
  const claimDataPointProperties =
    claimTypeDataPoints?.reduce<Record<string, AutomationPropertyMetadata>>((properties, dataPoint) => {
      if (!dataPoint.name) return properties;

      const propertyPath = createClaimDataPointPropertyPath(dataPoint.name);
      return {
        ...properties,
        [propertyPath]: getClaimDataPointPropertyMetadata(dataPoint, claimTextExpressionTypes),
      };
    }, {}) || {};

  return {
    expressionProperties: [
      ClaimAutomationPropertyPath.totalAmount,
      ClaimAutomationPropertyPath.contractExpiryDate,
      ClaimAutomationPropertyPath.contractEffectiveDate,
      ClaimAutomationPropertyPath.contractSalePriceCAD,
      ClaimAutomationPropertyPath.contractStatus,
      ClaimAutomationPropertyPath.contractMaxKm,
      ClaimAutomationPropertyPath.contractLegacyProductName,
      ClaimAutomationPropertyPath.claimCreatedAtUtc,
      ClaimAutomationPropertyPath.areAutomationRulesApplied,
      ClaimAutomationPropertyPath.legacyClaimTypeId,
      ClaimAutomationPropertyPath.claimWorkOrderNumber,
      ClaimAutomationPropertyPath.claimOdometerReading,
    ],
    effectProperties: [
      ClaimAutomationPropertyPath.claimStatus,
      ClaimAutomationPropertyPath.claimJobStatus,
      ClaimAutomationPropertyPath.claimAlerts,
      ...claimDataPointEffects.map((effect) => effect.propertyPath),
    ],
    supportedEffectTypes: [EffectTypes.Assign, EffectTypes.AssignFromSource, EffectTypes.AddOrUpdate],
    allowMultipleRules: true,
    allowMultipleExpressions: true,
    allowMultipleEffects: true,
    isPropertySelectorDisabled: false,
    defaultExpression: {
      propertyPath: ClaimAutomationPropertyPath.totalAmount,
      type: ExpressionTypes.Equal,
      value: '',
    },
    defaultEffects: [],
    properties: {
      [ClaimAutomationPropertyPath.totalAmount]: {
        inputType: AutomationInputTypes.Number,
        supportedExpressions: [...claimExpressionTypes],
      },
      [ClaimAutomationPropertyPath.contractExpiryDate]: {
        inputType: AutomationInputTypes.DateTime,
        supportedExpressions: [...claimDateExpressionTypes],
      },
      [ClaimAutomationPropertyPath.contractEffectiveDate]: {
        inputType: AutomationInputTypes.DateTime,
        supportedExpressions: [...claimDateExpressionTypes],
      },
      [ClaimAutomationPropertyPath.contractSalePriceCAD]: {
        inputType: AutomationInputTypes.Number,
        supportedExpressions: [...claimExpressionTypes],
      },
      [ClaimAutomationPropertyPath.contractStatus]: {
        inputType: AutomationInputTypes.Select,
        options: contractStatusOptions,
        supportedExpressions: claimStatusExpressionTypes,
      },
      [ClaimAutomationPropertyPath.contractMaxKm]: {
        inputType: AutomationInputTypes.Number,
        supportedExpressions: claimNumericExpressionTypes,
      },
      [ClaimAutomationPropertyPath.contractLegacyProductName]: {
        inputType: AutomationInputTypes.Text,
        supportedExpressions: claimTextExpressionTypes,
      },
      [ClaimAutomationPropertyPath.claimCreatedAtUtc]: {
        inputType: AutomationInputTypes.DateTime,
        supportedExpressions: [...claimDateExpressionTypes],
      },
      [ClaimAutomationPropertyPath.areAutomationRulesApplied]: {
        inputType: AutomationInputTypes.Boolean,
        supportedExpressions: claimBooleanExpressionTypes,
      },
      [ClaimAutomationPropertyPath.legacyClaimTypeId]: {
        inputType: AutomationInputTypes.Text,
        supportedExpressions: claimTextExpressionTypes,
      },
      [ClaimAutomationPropertyPath.claimStatus]: {
        inputType: AutomationInputTypes.Select,
        options: claimStatuses,
        supportedExpressions: claimStatusExpressionTypes,
      },
      [ClaimAutomationPropertyPath.claimJobStatus]: {
        inputType: AutomationInputTypes.Select,
        options: claimJobStatuses,
        supportedExpressions: claimStatusExpressionTypes,
      },
      [ClaimAutomationPropertyPath.claimWorkOrderNumber]: {
        inputType: AutomationInputTypes.Text,
        supportedExpressions: claimStatusExpressionTypes,
      },
      [ClaimAutomationPropertyPath.claimOdometerReading]: {
        inputType: AutomationInputTypes.Number,
        supportedExpressions: claimStatusExpressionTypes,
      },
      ...claimDataPointProperties,
    },
    effects: [
      {
        type: 'assign',
        propertyPath: ClaimAutomationPropertyPath.claimStatus,
        options: claimStatuses,
        supportedEffectTypes: [EffectTypes.Assign, EffectTypes.AssignFromSource],
      },
      {
        type: 'assign',
        propertyPath: ClaimAutomationPropertyPath.claimJobStatus,
        options: claimJobStatuses,
        supportedEffectTypes: [EffectTypes.Assign, EffectTypes.AssignFromSource],
      },
      {
        type: 'assign',
        propertyPath: ClaimAutomationPropertyPath.claimAlerts,
        supportedEffectTypes: [EffectTypes.AddOrUpdate],
      },
      ...claimDataPointEffects,
    ],
  };
}

export const createDealerClaimAutomationSchema = createClaimAutomationSchema;
export const createDealerGroupClaimAutomationSchema = createClaimAutomationSchema;
